import React, { useEffect, useState } from 'react'
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { useCurrentSociety } from '../../../hooks/useCurrentSociety'
import { useModal } from '../../../hooks/useModal'
import { useAuthenticatedUserId } from '../../../hooks/useAuthenticatedUserId'
import {
  isUserAdminInSociety,
  isUserBoardMemberInSociety,
} from '../../../helpers/society'
import { downloadCsv } from '../../../helpers/csv'
import { useDocumentTitle } from '../../../hooks/useDocumentTitle'
import { reverseDocumentTitle } from '../../../navigation/reverseDocumentTitle'
import { IllustrationChoices } from '../../../components/common/Illustration'
import { ErrorBoundary } from '../../../components/common/ErrorBoundary'
import { ViewBase } from '../../../components/common/ViewBase'
import { CreateUpdateEventModal } from '../../../components/society/Event/CreateUpdateEventModal'
import { IconChoices } from '../../../components/common/Icon'
import { ButtonVariant } from '../../../components/common/Button'
import { reverseUrl } from '../../../navigation/reverseUrl'
import { useEventTabs } from './useEventTabs'
import { RouteErrorView } from '../../error/RouteErrorView'
import { EventDetailInformation } from './EventDetailInformation'
import { EventDetailAttendants } from './EventDetailAttendants'
import {
  boardRoomEventQueries,
  getBoardroomEventAttendanceList,
} from '../../../api/boardroom-events'
import { eventQueries, getEventAttendanceList } from '../../../api/events'
import { NEvents } from '../../../interfaces/services/events.interfaces'
import { NBoardRoomEvents } from '../../../interfaces/services/boardroom-events.interfaces'
import { RemoveEventModal } from '../../../components/society/Event/RemoveEventModal'
import { i18nSocietyType } from '../../../i18n'

export enum EventDetailViewTabs {
  INFORMATION = 0,
  ATTENDANTS = 1,
}

interface EventDetailViewProps {
  boardRoomEvent?: boolean
}

export const EventDetailView = ({
  boardRoomEvent,
}: EventDetailViewProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { society } = useCurrentSociety()
  const location = useLocation()
  const navigate = useNavigate()
  const { getTabUrl, getTabState } = useEventTabs(boardRoomEvent)
  const [activeTab, setActiveTab] = useState<EventDetailViewTabs>(
    getTabState(location.pathname)
  )
  const { eventId } = useParams()

  const {
    show: showEditModal,
    open: openEditModal,
    close: closeEditModal,
  } = useModal()
  const {
    show: showDeleteConfirmationModal,
    open: openDeleteConfirmationModal,
    close: closeDeleteConfirmationModal,
  } = useModal()

  const userId = useAuthenticatedUserId() as string

  const userIsSocietyAdmin = isUserAdminInSociety(society, userId as string)
  const userIsBoardMember = isUserBoardMemberInSociety(society, userId)

  const { data: boardRoomEvents, isLoading: isBoardRoomEventsLoading } =
    useQuery(boardRoomEventQueries.getEventsPerSociety(society?._id))
  const { data: events, isLoading: isEventsLoading } = useQuery(
    eventQueries.getEventsPerSociety(society?._id)
  )

  const event = boardRoomEvent
    ? boardRoomEvents?.find((e) => e?._id === eventId)
    : events?.find((e) => e?._id === eventId)

  // When a new activeTab is set, we change the navigation to that tab as well
  useEffect(() => {
    const url = getTabUrl(activeTab)
    if (location.pathname !== url) {
      navigate(url)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab])

  useDocumentTitle(
    reverseDocumentTitle(
      boardRoomEvent ? 'management:calendar-detail' : 'society:calendar-detail',
      {
        '{{ eventName }}': event?.title,
        '{{ societyName }}': society?.name,
      }
    )
  )
  const exportAttendaceList = async (): Promise<void> => {
    if (eventId) {
      let resp:
        | AxiosResponse<NEvents.NGetAttendanceList.IResponse>
        | AxiosResponse<NBoardRoomEvents.NGetAttendanceList.IResponse>
      if (boardRoomEvent) {
        resp = await getBoardroomEventAttendanceList(eventId)
      } else {
        resp = await getEventAttendanceList(eventId)
      }
      const data = resp.data as NEvents.NGetAttendanceList.IResponse
      const csv = data.data.list.map((m) => ({
        Namn: m.name,
        Epost: m.email,
        Status:
          m.status === 'attending'
            ? translate(`eventDetailView.attendance.titles.${m.status}`)
            : translate(`eventDetailView.attendance.titles.notAttending`),
        Bostad: m.units,
        Kommentar: m.comment,
      }))

      downloadCsv(csv, `${data.data.event.title}-deltagarlista.csv`)
    }
  }

  const loading = boardRoomEvent ? isBoardRoomEventsLoading : isEventsLoading

  const buttons =
    userIsSocietyAdmin || userIsBoardMember
      ? [
          {
            label: `${translate('common.actions.download')} ${translate('eventDetailView.attendance.list').toLocaleLowerCase()}`,
            icon: IconChoices.DOCUMENT,
            onClick: exportAttendaceList,
          },
          {
            label: translate('common.actions.edit'),
            icon: IconChoices.EDIT,
            onClick: openEditModal,
          },

          {
            label: translate('common.actions.delete'),
            icon: IconChoices.DELETE_TRASH,
            variant: ButtonVariant.DANGER,
            onClick: openDeleteConfirmationModal,
          },
        ]
      : undefined

  const errorView = !event
    ? {
        title: translate('eventDetailView.errors.notFound.title'),
        subtitle: translate('eventDetailView.errors.notFound.subtitle'),
        illustration: IllustrationChoices.EMPTY,
      }
    : undefined

  const tabs = [
    {
      title: translate('eventDetailView.tabBar.information'),
    },
    ...(event?.attendanceEnabled
      ? [
          {
            title: translate('eventDetailView.tabBar.attendance'),
          },
        ]
      : []),
  ]

  return (
    <ErrorBoundary>
      <ViewBase
        title={
          event?.title ||
          translate(
            boardRoomEvent
              ? 'boardCalendarView.title'
              : 'societyCalendarListView.title',
            { context: i18nSocietyType(society?.societyTypeDisplayName) }
          )
        }
        showBackButton
        backUrl={reverseUrl(
          boardRoomEvent ? 'management:calendar' : 'society:calendar',
          {
            id: event?.societyId,
          }
        )}
        buttons={buttons}
        errorView={errorView}
        loading={loading}
        tabBar={{ tabs, setActiveTab, activeTab }}
      >
        {event && (
          <Routes>
            <Route
              path="information"
              element={
                <EventDetailInformation
                  event={event}
                  boardRoomEvent={boardRoomEvent}
                  setActiveTab={setActiveTab}
                />
              }
              errorElement={<RouteErrorView />}
            />
            <Route
              path="attendants"
              element={
                <EventDetailAttendants
                  event={event}
                  boardRoomEvent={boardRoomEvent}
                />
              }
              errorElement={<RouteErrorView />}
            />
          </Routes>
        )}
      </ViewBase>
      {showEditModal && (
        <CreateUpdateEventModal
          show={showEditModal}
          close={closeEditModal}
          event={event}
          boardRoomEvent={boardRoomEvent}
        />
      )}
      {showDeleteConfirmationModal && event && (
        <RemoveEventModal
          title={`${translate(
            'createUpdateEvent.confirmationModal.deleteTitle'
          )}`}
          show={showDeleteConfirmationModal}
          close={closeDeleteConfirmationModal}
          event={event}
          deleteMode
          boardRoomEvent={boardRoomEvent}
        />
      )}
    </ErrorBoundary>
  )
}
